import React, { useState, useEffect, useRef } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Send, Code2, MessageCircle, Loader2, Wifi, WifiOff, CheckCircle2, FileText, Save } from "lucide-react";

const LABELS = {
  new_scan: "New user scan", existing_scan: "Existing user scan", invalid_coupon: "Invalid coupon",
  used_coupon: "Already-used coupon", expired_coupon: "Expired coupon", wallet_reward: "Wallet reward",
  upi_reward: "UPI reward", gift_reward: "Gift redemption", failed_txn: "Failed UPI txn",
  campaign_message: "Campaign broadcast", below_threshold: "Below UPI threshold",
};

export default function Simulator() {
  const [scenarios, setScenarios] = useState([]);
  const [scenario, setScenario] = useState("new_scan");
  const [messages, setMessages] = useState([]);
  const [payloads, setPayloads] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showJson, setShowJson] = useState(true);
  const [status, setStatus] = useState(null);
  const [testMobile, setTestMobile] = useState("");
  const [testText, setTestText] = useState("Hi! This is a test message from Jewell Rewards \uD83C\uDFA8");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [tplEdits, setTplEdits] = useState({});
  const [savingTpl, setSavingTpl] = useState(null);
  const endRef = useRef(null);

  const loadCatalog = () => api.get("/whatsapp/events-catalog").then((r) => setCatalog(r.data.events)).catch(() => {});
  useEffect(() => { api.get("/simulator/scenarios").then((r) => setScenarios(r.data.scenarios)); }, []);
  useEffect(() => { api.get("/whatsapp/status").then((r) => setStatus(r.data)).catch(() => {}); loadCatalog(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const saveTpl = async (ev) => {
    setSavingTpl(ev.event);
    try {
      await api.post("/whatsapp/templates", { event: ev.event, template_id: tplEdits[ev.event] ?? ev.template_id ?? "", language: ev.language || "en" });
      toast.success(`Template saved for "${ev.label.split(" (")[0]}"`);
      await loadCatalog();
      setTplEdits((p) => { const n = { ...p }; delete n[ev.event]; return n; });
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingTpl(null); }
  };

  const sendTest = async () => {
    if (!testMobile.trim()) { toast.error("Enter a mobile number"); return; }
    setSending(true); setLastResult(null);
    try {
      const { data } = await api.post("/whatsapp/send-test", { mobile: testMobile, text: testText });
      setLastResult(data);
      if (data.mode === "karix" && String(data.status).startsWith("accepted")) toast.success("Live WhatsApp message accepted by Karix");
      else if (data.mode === "mock") toast.info("Sandbox mode — message simulated (set live creds to send for real)");
      else toast.error(`Send status: ${data.status}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  const run = async (sc) => {
    setBusy(true); setMessages([]); setPayloads([]);
    try {
      const { data } = await api.post("/simulator/run", { scenario: sc });
      // stream in messages for realism
      for (let i = 0; i < data.messages.length; i++) {
        await new Promise((r) => setTimeout(r, 320));
        setMessages((m) => [...m, data.messages[i]]);
      }
      setPayloads(data.webhook_payloads);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div><h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">WhatsApp Journey Simulator</h1>
          <p className="text-sm text-gray-500 mt-1">Replay conversation flows and send real WhatsApp messages via Karix</p></div>
        <button data-testid="sim-toggle-json" onClick={() => setShowJson(!showJson)} className="flex items-center gap-2 text-sm font-semibold border border-gray-300 px-3 py-2 rounded-lg hover:border-[#8B0000]"><Code2 className="h-4 w-4" /> {showJson ? "Hide" : "Show"} Payloads</button>
      </div>

      {/* Live connection + test send */}
      <div className="mb-5 rounded-xl border border-gray-200 bg-white p-4" data-testid="wa-live-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            {status?.live ? (
              <span data-testid="wa-status-live" className="inline-flex items-center gap-1.5 rounded-full bg-green-50 text-green-700 px-3 py-1 text-xs font-bold border border-green-200"><Wifi className="h-3.5 w-3.5" /> LIVE · Karix</span>
            ) : (
              <span data-testid="wa-status-sandbox" className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 px-3 py-1 text-xs font-bold border border-amber-200"><WifiOff className="h-3.5 w-3.5" /> SANDBOX</span>
            )}
            <span className="text-xs text-gray-500">
              {status ? (status.live
                ? `Sender ${status.sender}`
                : `${status.has_api_key ? "API key set" : "No API key"} · sender ${status.sender || "not set"}`) : "Checking…"}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-1 min-w-[260px] justify-end">
            <input data-testid="wa-test-mobile" value={testMobile} onChange={(e) => setTestMobile(e.target.value)}
              placeholder="Test mobile e.g. 9876543210"
              className="w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#8B0000]" />
            <input data-testid="wa-test-text" value={testText} onChange={(e) => setTestText(e.target.value)}
              placeholder="Message text"
              className="flex-1 max-w-sm border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#8B0000]" />
            <button data-testid="wa-send-test-btn" onClick={sendTest} disabled={sending}
              className="flex items-center gap-2 bg-[#075E54] hover:bg-[#064d45] text-white font-bold px-4 py-2 rounded-lg text-sm disabled:opacity-50">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Test
            </button>
          </div>
        </div>
        {lastResult && (
          <div data-testid="wa-test-result" className="mt-3 flex items-start gap-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600">
            <CheckCircle2 className="h-4 w-4 text-gray-400 mt-0.5" />
            <span className="font-mono break-all">mode: {lastResult.mode} · status: {lastResult.status}{lastResult.detail?.message_id ? ` · id: ${lastResult.detail.message_id}` : ""}</span>
          </div>
        )}
      </div>

      {/* Message template manager */}
      <div className="mb-5 rounded-xl border border-gray-200 bg-white p-4" data-testid="wa-templates-panel">
        <div className="flex items-center gap-2 mb-1"><FileText className="h-4 w-4 text-[#8B0000]" />
          <h3 className="font-display font-bold text-gray-900">Message Templates</h3></div>
        <p className="text-xs text-gray-500 mb-3">Map each automated message to its Karix-approved Template ID. Variables are sent in the order shown. Leave blank to fall back to plain session text (valid only within the 24h window).</p>
        <div className="space-y-2">
          {catalog.map((ev) => (
            <div key={ev.event} data-testid={`wa-tpl-row-${ev.event}`} className="flex flex-wrap items-center gap-3 border border-gray-100 rounded-lg px-3 py-2.5">
              <div className="flex-1 min-w-[220px]">
                <div className="text-sm font-semibold text-gray-900">{ev.label}</div>
                <div className="text-xs text-gray-400">{ev.variables.length ? `Variables: ${ev.variables.map((v, i) => `{{${i + 1}}} ${v}`).join(", ")}` : "No variables"}</div>
              </div>
              <input data-testid={`wa-tpl-input-${ev.event}`}
                value={tplEdits[ev.event] ?? ev.template_id ?? ""}
                onChange={(e) => setTplEdits((p) => ({ ...p, [ev.event]: e.target.value }))}
                placeholder="Karix Template ID"
                className="w-56 border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#8B0000]" />
              {ev.template_id ? <span className="text-xs font-semibold text-green-600">● live</span> : <span className="text-xs text-amber-500">● text fallback</span>}
              <button data-testid={`wa-tpl-save-${ev.event}`} onClick={() => saveTpl(ev)} disabled={savingTpl === ev.event}
                className="flex items-center gap-1.5 bg-[#8B0000] hover:bg-[#6B0000] text-white font-semibold px-3 py-2 rounded-lg text-sm disabled:opacity-50">
                {savingTpl === ev.event ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {scenarios.map((s) => (
          <button key={s} data-testid={`sim-scenario-${s}`} onClick={() => { setScenario(s); run(s); }}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold ${scenario === s ? "bg-[#8B0000] text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-[#8B0000]"}`}>{LABELS[s] || s}</button>
        ))}
      </div>

      <div className={`grid gap-6 ${showJson ? "lg:grid-cols-2" : "grid-cols-1 max-w-md"}`}>
        {/* Phone frame */}
        <div className="mx-auto w-full max-w-sm">
          <div className="rounded-[2rem] border-8 border-gray-900 bg-gray-900 shadow-2xl overflow-hidden">
            <div className="bg-[#075E54] text-white px-4 py-3 flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center"><MessageCircle className="h-5 w-5" /></div>
              <div><div className="font-semibold text-sm">Jewell Rewards</div><div className="text-[11px] text-white/70">business account</div></div>
            </div>
            <div className="bg-[#E5DDD5] h-[26rem] overflow-y-auto scrollbar-thin p-3 space-y-2" data-testid="sim-chat">
              {messages.length === 0 && !busy && <div className="text-center text-gray-500 text-sm py-10">Pick a scenario above to replay the conversation</div>}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm shadow-sm ${m.from === "user" ? "bg-[#DCF8C6]" : "bg-white"}`}>
                    <div className="whitespace-pre-line text-gray-800" dangerouslySetInnerHTML={{ __html: m.text.replace(/\*(.*?)\*/g, "<b>$1</b>") }} />
                    {m.buttons?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {m.buttons.map((b, j) => <div key={j} className="text-center text-[#8B0000] font-semibold text-xs border-t border-gray-100 pt-1.5">{b}</div>)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {busy && <div className="flex justify-start"><div className="bg-white rounded-lg px-3 py-2 shadow-sm"><Loader2 className="h-4 w-4 animate-spin text-gray-400" /></div></div>}
              <div ref={endRef} />
            </div>
            <div className="bg-[#F0F0F0] px-3 py-2 flex items-center gap-2">
              <div className="flex-1 bg-white rounded-full px-3 py-2 text-sm text-gray-400">Type a message…</div>
              <button data-testid="whatsapp-sim-send-btn" onClick={() => run(scenario)} className="h-9 w-9 rounded-full bg-[#075E54] text-white flex items-center justify-center"><Send className="h-4 w-4" /></button>
            </div>
          </div>
        </div>

        {/* Payload viewer */}
        {showJson && (
          <div className="bg-[#111827] rounded-xl p-4 overflow-auto scrollbar-thin max-h-[32rem]" data-testid="sim-payloads">
            <div className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-2">Webhook Payloads</div>
            <pre className="text-xs text-green-300 font-mono whitespace-pre-wrap">{JSON.stringify(payloads, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
}
